import { useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { db, functions, storage } from '../../firebase';
import { IMAGE_OUTPUTS, PRESET_DETAILS, VIDEO_OUTPUTS, isImageFile, isVideoFile } from '../../conversionPresets';
import { MAX_BATCH_FILES, STUCK_PROCESSING_MS } from './constants';
import { formatDateTime, getTimestampMillis, isStuckProcessing, statusBadgeClass, truncateFileName } from './utils';
import ConfirmRetryModal from './components/ConfirmRetryModal';
import ExpandedNoticeModal from './components/ExpandedNoticeModal';
import JobsTable from './components/JobsTable';
import NoticeCenter from './components/NoticeCenter';
import UploadPanel from './components/UploadPanel';

const getOutputsForFile = (file) => {
  if (isImageFile(file)) return IMAGE_OUTPUTS;
  if (isVideoFile(file)) return VIDEO_OUTPUTS;
  return [];
};

const getNoticeFeedStorageKey = (uid) => `format-converter:notice-feed:${uid || 'anonymous'}`;
const getActiveNoticeStorageKey = (uid) => `format-converter:active-notice:${uid || 'anonymous'}`;

const readPersistedNoticeFeed = (uid) => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(getNoticeFeedStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry) => entry && typeof entry.key === 'string' && typeof entry.message === 'string')
      .slice(0, 10);
  } catch (_error) {
    return [];
  }
};

const readPersistedActiveNoticeKey = (uid) => {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(getActiveNoticeStorageKey(uid)) || '';
  } catch (_error) {
    return '';
  }
};

function FormatConverterProject({ user: currentUser = null }) {
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [activeUploadFileName, setActiveUploadFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [submitInfo, setSubmitInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingJobId, setDeletingJobId] = useState('');
  const [downloadingJobId, setDownloadingJobId] = useState('');
  const [retryingJobId, setRetryingJobId] = useState('');
  const [retryStatusByJobId, setRetryStatusByJobId] = useState({});
  const [noticeFeed, setNoticeFeed] = useState(() => readPersistedNoticeFeed(currentUser?.uid));
  const [activeNoticeKey, setActiveNoticeKey] = useState(() => readPersistedActiveNoticeKey(currentUser?.uid));
  const [isNoticeListOpen, setIsNoticeListOpen] = useState(false);
  const [expandedNotice, setExpandedNotice] = useState(null);
  const [isDropOverSelect, setIsDropOverSelect] = useState(false);
  const [isDropOverAdd, setIsDropOverAdd] = useState(false);
  const [confirmRetryJob, setConfirmRetryJob] = useState(null);
  const [jobs, setJobs] = useState([]);

  const addFilesInputRef = useRef(null);
  const observedJobStateRef = useRef({});

  const showFloatingNotice = (message, tone = 'info', jobId = '', jobLabel = '') => {
    if (!message) return;

    const noticeKey = jobId ? `job:${jobId}` : 'general';
    const title = jobLabel
      ? `Job: ${truncateFileName(jobLabel, 34)}`
      : jobId
        ? `Job ${jobId.slice(0, 8)}`
        : 'System';

    setNoticeFeed((prev) => {
      const now = Date.now();
      const next = [...prev];
      const index = next.findIndex((entry) => entry.key === noticeKey);
      const notice = {
        key: noticeKey,
        tone,
        title,
        message,
        updatedAt: now
      };
      if (index >= 0) {
        next[index] = notice;
      } else {
        next.push(notice);
      }
      next.sort((a, b) => b.updatedAt - a.updatedAt);
      return next.slice(0, 10);
    });
    setActiveNoticeKey(noticeKey);
  };

  useEffect(() => {
    if (!currentUser) {
      setJobs([]);
      return undefined;
    }

    const jobsQuery = query(
      collection(db, 'conversionJobs'),
      where('uid', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsub = onSnapshot(jobsQuery, (snapshot) => {
      const next = snapshot.docs.map((snapshotDoc) => ({ id: snapshotDoc.id, ...snapshotDoc.data() }));
      setJobs(next);
    });

    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!noticeFeed.length) {
      setActiveNoticeKey('');
      setIsNoticeListOpen(false);
      return;
    }

    if (!noticeFeed.some((entry) => entry.key === activeNoticeKey)) {
      setActiveNoticeKey(noticeFeed[0].key);
    }
  }, [noticeFeed, activeNoticeKey]);

  useEffect(() => {
    if (!currentUser?.uid || typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(getNoticeFeedStorageKey(currentUser.uid), JSON.stringify(noticeFeed));
    } catch (_error) {
      // Ignore sessionStorage write issues (quota/private mode).
    }
  }, [noticeFeed, currentUser]);

  useEffect(() => {
    if (!currentUser?.uid || typeof window === 'undefined') return;
    try {
      if (activeNoticeKey) {
        window.sessionStorage.setItem(getActiveNoticeStorageKey(currentUser.uid), activeNoticeKey);
      } else {
        window.sessionStorage.removeItem(getActiveNoticeStorageKey(currentUser.uid));
      }
    } catch (_error) {
      // Ignore sessionStorage write issues (quota/private mode).
    }
  }, [activeNoticeKey, currentUser]);

  useEffect(() => {
    const previousById = observedJobStateRef.current;
    const nextById = {};

    jobs.forEach((job) => {
      const status = job.status || 'queued';
      const error = job.error || '';
      const updatedAtMs = getTimestampMillis(job.updatedAt) || 0;
      const previous = previousById[job.id];
      const retryState = retryStatusByJobId[job.id];

      const isFirstSeen = !previous;
      const hasStatusChanged = Boolean(previous) && previous.status !== status;
      const hasErrorChanged = Boolean(previous) && previous.error !== error && Boolean(error);
      const shouldSuppressFromRetryWatcher = retryState?.phase === 'waiting';
      const shouldNotifyFailure =
        status === 'failed' &&
        !shouldSuppressFromRetryWatcher &&
        (isFirstSeen || hasStatusChanged || hasErrorChanged);
      const shouldNotifyComplete = status === 'completed' && !isFirstSeen && hasStatusChanged;

      if (shouldNotifyFailure) {
        showFloatingNotice(`Failed: ${error || 'Conversion failed.'}`, 'error', job.id, job.input?.name || '');
      } else if (shouldNotifyComplete) {
        showFloatingNotice('Update received: completed (100%).', 'success', job.id, job.input?.name || '');
      }

      nextById[job.id] = { status, error, updatedAtMs };
    });

    observedJobStateRef.current = nextById;
  }, [jobs, retryStatusByJobId]);

  useEffect(() => {
    let didChange = false;
    const next = { ...retryStatusByJobId };

    Object.entries(retryStatusByJobId).forEach(([jobId, state]) => {
      if (state.phase !== 'waiting') return;

      const current = jobs.find((entry) => entry.id === jobId);
      if (!current) return;

      const currentProgress = current.progress ?? 0;
      const currentStatus = current.status || 'queued';
      const currentUpdatedAtMs = getTimestampMillis(current.updatedAt) || 0;
      const base = state.baseline || {};
      const hasProgressMoved = currentProgress !== base.progress;
      const hasStatusMoved = currentStatus !== base.status;
      const hasTimestampMoved = currentUpdatedAtMs > (base.updatedAtMs || 0);

      if (hasProgressMoved || hasStatusMoved || hasTimestampMoved) {
        const nextMessage =
          currentStatus === 'failed' && current.error
            ? `Failed: ${current.error}`
            : `Update received: ${currentStatus} (${currentProgress}%).`;
        next[jobId] = {
          phase: 'updated',
          message: nextMessage
        };
        showFloatingNotice(
          nextMessage,
          currentStatus === 'failed' ? 'error' : 'info',
          jobId,
          current.input?.name || ''
        );
        didChange = true;
      }
    });

    if (didChange) {
      setRetryStatusByJobId(next);
    }
  }, [jobs, retryStatusByJobId]);

  useEffect(() => {
    if (submitError) showFloatingNotice(submitError, 'error');
  }, [submitError]);

  useEffect(() => {
    if (submitInfo) showFloatingNotice(submitInfo, 'info');
  }, [submitInfo]);

  const addFilesToQueue = (incomingFileList) => {
    const incoming = Array.from(incomingFileList || []);
    if (!incoming.length) return;

    const nextEntries = [];
    let skippedCount = 0;

    incoming.forEach((candidate) => {
      const outputs = getOutputsForFile(candidate);
      if (!outputs.length) {
        skippedCount += 1;
        return;
      }

      nextEntries.push({
        id: crypto.randomUUID(),
        file: candidate,
        format: outputs[0].value
      });
    });

    const remainingSlots = Math.max(0, MAX_BATCH_FILES - queuedFiles.length);
    const acceptedEntries = nextEntries.slice(0, remainingSlots);
    const overflowCount = Math.max(0, nextEntries.length - acceptedEntries.length);

    if (acceptedEntries.length) {
      setQueuedFiles((prev) => [...prev, ...acceptedEntries]);
    }

    const messages = [];
    if (skippedCount) {
      messages.push(`${skippedCount} file(s) were skipped. Only image/video files are supported.`);
    }
    if (overflowCount) {
      messages.push(`Batch limit reached: max ${MAX_BATCH_FILES} files per convert.`);
    }
    setSubmitError(messages.join(' '));
  };

  const handleQueueFileChange = (event) => {
    addFilesToQueue(event.target.files);
    event.target.value = '';
  };

  const handleAddFilesClick = () => {
    if (queuedFiles.length >= MAX_BATCH_FILES) return;
    addFilesInputRef.current?.click();
  };

  const handleDropFiles = (event, target) => {
    event.preventDefault();
    event.stopPropagation();
    if (target === 'select') setIsDropOverSelect(false);
    if (target === 'add') setIsDropOverAdd(false);
    addFilesToQueue(event.dataTransfer?.files);
  };

  const handleQueuedFormatChange = (fileId, nextFormat) => {
    setQueuedFiles((prev) => prev.map((entry) => (entry.id === fileId ? { ...entry, format: nextFormat } : entry)));
  };

  const handleRemoveQueuedFile = (fileId) => {
    setQueuedFiles((prev) => prev.filter((entry) => entry.id !== fileId));
  };

  const handleCreateJob = async (event) => {
    event.preventDefault();

    if (!queuedFiles.length || !currentUser) {
      setSubmitError('Add at least one file before converting.');
      return;
    }

    setSubmitError('');
    setSubmitInfo('');
    setIsSubmitting(true);
    setUploadProgress(0);
    setActiveUploadFileName('');

    try {
      let createdCount = 0;
      let failedCount = 0;

      for (const queued of queuedFiles) {
        const preset = PRESET_DETAILS[queued.format];
        if (!preset) {
          failedCount += 1;
          continue;
        }

        try {
          setActiveUploadFileName(queued.file.name);
          const extension = queued.file.name.includes('.') ? queued.file.name.split('.').pop() : 'bin';
          const objectPath = `uploads/${currentUser.uid}/${Date.now()}-${crypto.randomUUID()}.${extension}`;
          const uploadRef = ref(storage, objectPath);
          const task = uploadBytesResumable(uploadRef, queued.file, {
            contentType: queued.file.type || 'application/octet-stream'
          });

          const uploadedPath = await new Promise((resolve, reject) => {
            task.on(
              'state_changed',
              (snapshot) => {
                const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                setUploadProgress(pct);
              },
              reject,
              () => resolve(task.snapshot.ref.fullPath)
            );
          });

          const inputDownloadUrl = await getDownloadURL(uploadRef);

          await addDoc(collection(db, 'conversionJobs'), {
            uid: currentUser.uid,
            input: {
              name: queued.file.name,
              size: queued.file.size,
              mimeType: queued.file.type,
              path: uploadedPath,
              downloadURL: inputDownloadUrl
            },
            output: {
              format: queued.format,
              ext: preset.ext,
              path: null,
              downloadURL: null
            },
            preset: {
              key: queued.format,
              ffmpegArgs: preset.ffmpegArgs
            },
            status: 'queued',
            progress: 0,
            error: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

          createdCount += 1;
        } catch (jobError) {
          failedCount += 1;
          // eslint-disable-next-line no-console
          console.error('Could not create one of the queued jobs:', jobError);
        }
      }

      if (createdCount > 0) {
        if (failedCount) {
          setSubmitInfo(`${failedCount} file(s) failed to queue.`);
        }
        setQueuedFiles([]);
      } else {
        setSubmitError('Could not create any conversion jobs from the selected files.');
      }

      setUploadProgress(0);
      setActiveUploadFileName('');
    } catch (error) {
      setSubmitError(error.message || 'Could not create conversion job.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    setDeletingJobId(jobId);

    try {
      await deleteDoc(doc(db, 'conversionJobs', jobId));
    } catch (error) {
      const message = error.message || 'Could not delete this job.';
      const job = jobs.find((entry) => entry.id === jobId);
      showFloatingNotice(message, 'error', jobId, job?.input?.name || '');
    } finally {
      setDeletingJobId('');
    }
  };

  const handleDownload = async (job) => {
    if (!job?.output?.downloadURL) return;

    setDownloadingJobId(job.id);

    try {
      const inputName = job.input?.name || 'converted-file';
      const baseName = inputName.includes('.') ? inputName.slice(0, inputName.lastIndexOf('.')) : inputName;
      const ext = job.output?.ext || 'bin';
      const filename = `${baseName}.${ext}`;
      const downloadUrl = new URL(job.output.downloadURL);
      downloadUrl.searchParams.set('response-content-disposition', `attachment; filename="${filename}"`);
      downloadUrl.searchParams.set('response-content-type', 'application/octet-stream');

      const response = await fetch(downloadUrl.toString(), { method: 'GET', mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Download failed (${response.status}).`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      const message = error.message || 'Could not download file.';
      showFloatingNotice(message, 'error', job.id, job.input?.name || '');
    } finally {
      setDownloadingJobId('');
    }
  };

  const handleRetryJob = async (job, forceRetry = false) => {
    if (!job) return;

    setSubmitInfo('');
    setRetryingJobId(job.id);
    setRetryStatusByJobId((prev) => ({
      ...prev,
      [job.id]: {
        phase: 'sending',
        message: 'Sending retry request...'
      }
    }));
    showFloatingNotice('Sending retry request...', 'info', job.id, job.input?.name || '');

    try {
      const startConversion = httpsCallable(functions, 'startConversion', {
        timeout: 60 * 60 * 1000
      });
      const response = await startConversion({
        jobId: job.id,
        force: forceRetry || isStuckProcessing(job, STUCK_PROCESSING_MS)
      });
      const result = response?.data || {};
      const baseline = {
        status: job.status || 'queued',
        progress: job.progress ?? 0,
        updatedAtMs: getTimestampMillis(job.updatedAt) || 0
      };

      if (result.status === 'completed') {
        setRetryStatusByJobId((prev) => ({
          ...prev,
          [job.id]: {
            phase: 'updated',
            message: 'Job is already completed.'
          }
        }));
        showFloatingNotice('Job is already completed.', 'success', job.id, job.input?.name || '');
        return;
      }

      if (result.status === 'processing' && result.message) {
        setRetryStatusByJobId((prev) => ({
          ...prev,
          [job.id]: {
            phase: 'waiting',
            message: result.message,
            baseline
          }
        }));
        showFloatingNotice(result.message, 'info', job.id, job.input?.name || '');
        return;
      }

      setRetryStatusByJobId((prev) => ({
        ...prev,
        [job.id]: {
          phase: 'waiting',
          message: 'Retry accepted. Waiting for worker update...',
          baseline
        }
      }));
      showFloatingNotice('Retry accepted. Waiting for worker update...', 'info', job.id, job.input?.name || '');
    } catch (error) {
      const errorCode = String(error?.code || '').toLowerCase();
      const isDeadlineExceeded =
        errorCode.includes('deadline-exceeded') || errorCode.includes('deadline_exceeded');

      if (isDeadlineExceeded) {
        const baseline = {
          status: job.status || 'queued',
          progress: job.progress ?? 0,
          updatedAtMs: getTimestampMillis(job.updatedAt) || 0
        };
        setRetryStatusByJobId((prev) => ({
          ...prev,
          [job.id]: {
            phase: 'waiting',
            message: 'Worker is still running. Waiting for job update...',
            baseline
          }
        }));
        showFloatingNotice('Worker is still running. Waiting for job update...', 'info', job.id, job.input?.name || '');
        return;
      }

      setRetryStatusByJobId((prev) => ({
        ...prev,
        [job.id]: {
          phase: 'error',
          message: error.message || 'Retry failed.'
        }
      }));
      showFloatingNotice(error.message || 'Retry failed.', 'error', job.id, job.input?.name || '');
    } finally {
      setRetryingJobId('');
    }
  };

  const handleRetryClick = (job) => {
    if (!job) return;
    if (job.status === 'processing') {
      setConfirmRetryJob(job);
      return;
    }
    handleRetryJob(job);
  };

  const activeNotice = noticeFeed.find((entry) => entry.key === activeNoticeKey) || noticeFeed[0] || null;

  if (!currentUser) {
    return null;
  }

  return (
    <main className={`text-slate-900 ${activeNotice ? 'pb-28 sm:pb-0' : ''}`}>
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <UploadPanel
          queuedFiles={queuedFiles}
          isDropOverSelect={isDropOverSelect}
          setIsDropOverSelect={setIsDropOverSelect}
          isDropOverAdd={isDropOverAdd}
          setIsDropOverAdd={setIsDropOverAdd}
          addFilesInputRef={addFilesInputRef}
          handleQueueFileChange={handleQueueFileChange}
          handleDropFiles={handleDropFiles}
          handleQueuedFormatChange={handleQueuedFormatChange}
          handleRemoveQueuedFile={handleRemoveQueuedFile}
          handleAddFilesClick={handleAddFilesClick}
          handleCreateJob={handleCreateJob}
          getOutputsForFile={getOutputsForFile}
          isSubmitting={isSubmitting}
          uploadProgress={uploadProgress}
          activeUploadFileName={activeUploadFileName}
        />

        <JobsTable
          jobs={jobs}
          formatDateTime={formatDateTime}
          truncateFileName={truncateFileName}
          statusBadgeClass={statusBadgeClass}
          downloadingJobId={downloadingJobId}
          retryingJobId={retryingJobId}
          deletingJobId={deletingJobId}
          handleDownload={handleDownload}
          handleRetryClick={handleRetryClick}
          handleDeleteJob={handleDeleteJob}
        />
      </div>

      <NoticeCenter
        activeNotice={activeNotice}
        noticeFeed={noticeFeed}
        isNoticeListOpen={isNoticeListOpen}
        setIsNoticeListOpen={setIsNoticeListOpen}
        setActiveNoticeKey={setActiveNoticeKey}
        setExpandedNotice={setExpandedNotice}
      />

      <ExpandedNoticeModal expandedNotice={expandedNotice} onClose={() => setExpandedNotice(null)} />

      <ConfirmRetryModal
        confirmRetryJob={confirmRetryJob}
        onCancel={() => setConfirmRetryJob(null)}
        onConfirm={() => {
          const selected = confirmRetryJob;
          setConfirmRetryJob(null);
          handleRetryJob(selected, true);
        }}
      />
    </main>
  );
}

export default FormatConverterProject;
